/* eslint-disable react-hooks/exhaustive-deps */
import ToggleBookmarkButton from "components/bookmarks/ToggleBookmarkButton";
import Bookkeeper from "components/company/Bookkeeper";
import IndustrySector from "components/company/IndustrySector";
import BrazilianCompanyIssuerExternalLinks from "components/company/v2/BrazilianCompanyIssuerExternalLinks";
import InBookmarksTag from "components/company/v2/InBookmarksTag";
import InPortfolioIndicator from "components/company/v2/InPortfolioTag";
import classNames from "components/ui/classNames";
import { formatDistanceStrict, getYear, lightFormat } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import { Dialog, DialogCloseX, palette, shades } from "lib/miolo";
import formatCep from "lib/utils/formatCep";
import formatCnpj from "lib/utils/formatCnpj";
import mediaQueries from "lib/utils/layout/mediaQueries";
import parseDateYmd from "lib/utils/parseDateYmd";
import numeral from "numeral";
import { useState } from "react";
import { IAssetType } from "types/graphqlTypes";

interface IBrazilianCompanyIssuerHeaderCardProps {
  issuer: IBrazilianCompanyIssuerTopLevelFragment;
}

function BrazilianCompanyIssuerHeaderCard({ issuer }: IBrazilianCompanyIssuerHeaderCardProps) {
  const [isShowingMoreDetails, setShowMoreDetails] = useState(false);

  return (
    <div className="space-y-4">
      <MoreDetailsDialog issuer={issuer} isOpen={isShowingMoreDetails} onDismiss={() => setShowMoreDetails(false)} />

      <div className="space-y-2 font-aeonik">
        <div className="flex items-center">
          <h1 className="m-0 items-baseline text-3xl font-bold text-gray-800">{issuer.name}</h1>

          <div className="ml-2 inline-flex">
            <ToggleBookmarkButton issuer={issuer} viewerDidBookmark={issuer.viewerDidBookmark} />
          </div>

          {issuer.isBrazilianDepositaryReceipt && (
            <div className="ml-2">
              <span
                title="Brazilian Depositary Receipt"
                className="rounded-lg bg-blue-600 px-2 py-1 text-sm font-bold italic text-white"
              >
                BDR
              </span>
            </div>
          )}
        </div>

        {issuer.securities.length > 0 && (
          <div className="space-x-2">
            {issuer.securities.map((security) => {
              return (
                <strong
                  key={security.tickerSymbol}
                  className="rounded-md bg-violet-50 px-2 py-1 text-xs font-bold tracking-wider text-violet-600 dark:bg-gray-700 dark:text-gray-300"
                >
                  {security.tickerSymbol}
                </strong>
              );
            })}
          </div>
        )}

        {issuer.standardLogo && (
          <img
            role="presentation"
            src={issuer.standardLogo.url}
            width={191}
            height={58}
            alt={issuer.name}
            draggable={false}
            className="absolute top-8 right-8 hidden h-14 select-none object-contain object-right xl:block"
          />
        )}

        {issuer.niceLogo && (
          <img
            role="presentation"
            src={issuer.niceLogo.url}
            width={48}
            height={48}
            alt={issuer.name}
            draggable={false}
            className="absolute top-8 right-8 block h-12 w-12 select-none object-contain object-right squircle xl:hidden"
          />
        )}
      </div>

      <p className="max-w-xl font-aeonik text-sm font-normal tracking-wide text-gray-500 md:text-base">
        {issuer.businessDescription}
      </p>

      <div>
        <IndustrySector
          assetType={IAssetType.BrazilianCompany}
          industrySector={issuer.sector}
          className="tracking-wide"
        />
      </div>

      <div className="flex flex-col items-center space-y-4 xl:flex-row xl:space-x-2 xl:space-y-0">
        <button
          type="button"
          onClick={() => setShowMoreDetails(true)}
          className={classNames(
            "flex rounded-xl bg-gray-100 p-2 px-4 font-aeonik text-sm font-medium tracking-wide text-gray-600",
            "w-full justify-center xl:w-auto",
            "border border-transparent",
            "hover:border-gray-300",
          )}
        >
          Ver mais detalhes
        </button>

        <BrazilianCompanyIssuerExternalLinks issuer={issuer} />
      </div>

      <div className="space-x-2">
        {issuer.isInPortfolio && <InPortfolioIndicator />}
        {issuer.viewerDidBookmark && <InBookmarksTag />}
      </div>
    </div>
  );
}

export default BrazilianCompanyIssuerHeaderCard;

interface IMoreDetailsDialogProps {
  issuer: IBrazilianCompanyIssuerTopLevelFragment;
  isOpen: boolean;
  onDismiss: () => void;
}

function MoreDetailsDialog({ issuer, isOpen, onDismiss }: IMoreDetailsDialogProps) {
  const cvmRegistrationDate = issuer.cvmRegistrationDate ? parseDateYmd(issuer.cvmRegistrationDate) : null;

  return (
    <Dialog
      aria-label="Informações adicionais da empresa, endereço, auditoria, etc."
      isOpen={isOpen}
      onDismiss={onDismiss}
      css={mediaQueries({
        position: "relative",
        display: "block",
        boxSizing: "border-box",
        minWidth: 320,
        maxWidth: 780,
        margin: [12, "10vh auto 10vh auto", "10vh auto 10vh auto", "10vh auto"],
        padding: [24, 24, 24, 24],
        borderRadius: [6, 6, 6, 12],
      })}
    >
      <div css={{ display: "grid", gridRowGap: 20 }}>
        <header>
          <div
            css={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <h1
              css={{
                margin: 0,
                color: palette.bigStone,
                fontWeight: "bold",
                fontSize: 28,
                fontFamily: "museo-sans-condensed",
                lineHeight: 1.33,
                letterSpacing: 1,
              }}
            >
              Detalhes da Empresa
            </h1>

            <DialogCloseX onClick={onDismiss} />
          </div>

          <p
            css={{
              margin: 0,
              marginTop: 4,
              color: palette.lynch,
              fontWeight: 500,
              fontSize: 14,
              fontFamily: "museo-sans-condensed",
              lineHeight: 1.33,
              letterSpacing: 1,
            }}
          >
            Informações adicionais da empresa, endereço, auditoria, etc.
          </p>
        </header>

        <div css={{ marginLeft: -24, marginRight: -24 }}>
          <hr
            css={{
              margin: 0,
              border: "none",
              borderTop: `solid 1px ${palette.catskillWhite}`,
            }}
          />

          <div>
            <dl
              css={{
                margin: 0,
                fontFamily: "museo-sans-condensed",
                lineHeight: 1.33,
                "& > div": {
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  padding: "20px 24px",
                  "&:nth-of-type(odd)": {
                    backgroundColor: palette.catskillWhite,
                  },
                  "& > dt, dd": {
                    margin: 0,
                  },
                  "& > dt": {
                    color: palette.blueBayoux,
                    fontWeight: 500,
                    fontSize: 14,
                    letterSpacing: 1,
                  },
                  "& > dd": {
                    color: palette.pickledBluewood,
                    fontWeight: 500,
                    fontSize: 14,
                    letterSpacing: 1,
                  },
                },
                "& a": {
                  color: palette.link,
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                },
              }}
            >
              {issuer.headquarters && issuer.headquarters.country === "Brasil" && (
                <div>
                  <dt>Sede</dt>
                  <dd>
                    <Address headquarters={issuer.headquarters} />
                  </dd>
                </div>
              )}

              {issuer.foundedYear && (
                <div>
                  <dt>Ano de Fundação</dt>
                  <dd>{issuer.foundedYear}</dd>
                </div>
              )}

              {issuer.employeeNumbers && (
                <div>
                  <dt>N° de Empregados</dt>
                  <dd>
                    {numeral(issuer.employeeNumbers.totalOfEmployees).format("0,0").replace(",", ".")}{" "}
                    <span className="text-xs font-medium italic text-gray-600">
                      ({getYear(parseDateYmd(issuer.employeeNumbers.asOfDate))})
                    </span>
                  </dd>
                </div>
              )}

              {cvmRegistrationDate && (
                <div>
                  <dt>Listagem na CVM</dt>
                  <dd>
                    {lightFormat(cvmRegistrationDate, "dd/MM/yyyy")}{" "}
                    <span
                      css={{
                        fontWeight: 500,
                        fontStyle: "italic",
                        borderRadius: 12,
                        color: shades.greys.variants[3],
                        fontSize: 12,
                      }}
                    >
                      ({distanceSinceToday(cvmRegistrationDate)})
                    </span>
                  </dd>
                </div>
              )}

              {issuer.website && (
                <div>
                  <dt>Website</dt>
                  <dd>
                    <a href={issuer.website} target="_blank" rel="noopener noreferrer">
                      {new URL(issuer.website).host}
                    </a>
                  </dd>
                </div>
              )}

              <div>
                <dt>CNPJ</dt>
                <dd>{formatCnpj(issuer.cnpj)}</dd>
              </div>

              {issuer.bookkeeper && (
                <div>
                  <dt>Escriturador</dt>
                  <dd>
                    <div css={{ position: "relative" }}>
                      <Bookkeeper bookkeeper={issuer.bookkeeper} side="top" />
                    </div>
                  </dd>
                </div>
              )}

              {issuer.auditor && (
                <div>
                  <dt>Auditoria</dt>
                  <dd>
                    <div>{issuer.auditor.name}</div>

                    <div css={{ marginTop: 12 }}>
                      <span
                        css={{
                          fontWeight: 500,
                          fontStyle: "italic",
                          borderRadius: 12,
                          color: shades.greys.variants[3],
                          fontSize: 12,
                        }}
                      >
                        Desde {lightFormat(parseDateYmd(issuer.auditor.dateSince), "dd/MM/yyyy")}
                      </span>
                    </div>
                  </dd>
                </div>
              )}

              <div>
                <dt>Fatores de Risco</dt>
                <dd>
                  <a target="_blank" rel="noopener noreferrer" href={issuer.riskFactorsUrl}>
                    Visualizar
                  </a>
                </dd>
              </div>

              <div>
                <dt>Breve Histórico</dt>
                <dd>
                  <a target="_blank" rel="noopener noreferrer" href={issuer.briefHistoryUrl}>
                    Visualizar
                  </a>
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

function distanceSinceToday(pastDate: Date): string {
  const distanceStrict = formatDistanceStrict(pastDate, new Date(), {
    locale: ptBR,
  });
  return `há ${distanceStrict}`;
}

interface IAddressProps {
  headquarters: NonNullable<IBrazilianCompanyIssuerTopLevelFragment["headquarters"]>;
}

function Address({ headquarters }: IAddressProps) {
  if (headquarters.country === "Brasil") {
    return (
      <div
        css={{
          "& > p": {
            margin: 0,
            color: palette.pickledBluewood,
            fontWeight: 500,
            fontSize: 14,
            fontFamily: "museo-sans-condensed",
            lineHeight: 1.33,
            letterSpacing: 1,
          },
        }}
      >
        <p>
          {headquarters.street}, {headquarters.neighborhood}
        </p>

        <p>
          {headquarters.city}, {headquarters.state}
        </p>

        <p>{formatCep(headquarters.postalCode)}</p>
      </div>
    );
  }

  return <div />;
}
